import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePlans from '../hooks/usePlans';
import PlanCards from '../components/PlanCards';
import {
  Mail, MessageSquare, ShieldCheck, BarChart2, Zap, Users,
  ArrowRight, ChevronRight, Menu, X
} from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { plans, error: plansError, reload } = usePlans();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) return null;

  return (
    <div className="min-h-screen bg-white font-sans" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ─────────────────────────────────────────────────────────── NAV */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md shadow-indigo-200">
                <Zap className="w-4.5 h-4.5 text-white" size={18} />
              </div>
              <span className="text-lg font-bold text-gray-900 tracking-tight">Outreach Engine</span>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                Pricing
              </Link>
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                Sign In
              </Link>
              <Link
                to="/pricing#plan"
                className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200"
              >
                Get Started
                <ArrowRight size={14} />
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3">
            <Link to="/pricing" className="block text-sm font-medium text-gray-700 py-2">Pricing</Link>
            <Link to="/login" className="block text-sm font-medium text-gray-700 py-2">Sign In</Link>
            <Link to="/pricing#plan" className="block w-full text-center bg-indigo-600 text-white text-sm font-semibold px-5 py-3 rounded-lg">
              Get Started
            </Link>
          </div>
        )}
      </header>

      {/* ──────────────────────────────────────────────────────────── HERO */}
      <section className="relative pt-28 pb-24 px-6 overflow-hidden">
        {/* Background gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-violet-50" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-radial from-indigo-100/60 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-violet-100/40 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white border border-indigo-100 text-indigo-600 text-xs font-semibold px-4 py-1.5 rounded-full mb-8 shadow-sm">
            <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />
            Cold email + SMS + verification in one platform
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6">
            Reach more leads.<br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-500 bg-clip-text text-transparent">
              Close more deals.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed mb-10">
            The outreach platform combining cold email, SMS, prospect search, and email
            verification on one simple credit balance.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <Link
              to="/pricing#plan"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-semibold text-base px-8 py-4 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-200 hover:-translate-y-0.5"
            >
              Start Now
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/pricing"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-gray-700 font-semibold text-base px-8 py-4 rounded-xl border border-gray-200 hover:border-indigo-300 hover:text-indigo-600 transition-all hover:-translate-y-0.5"
            >
              View Pricing
              <ChevronRight size={18} />
            </Link>
          </div>

          {/* Trust note */}
          <p className="text-sm text-gray-400">Set up in minutes · Cancel anytime</p>
        </div>

        {/* Stats row */}
        <div className="relative max-w-4xl mx-auto mt-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { val: '3 Plans', label: 'Pick your monthly credits' },
              { val: '1 Balance', label: 'Search and verify credits' },
              { val: '3 Channels', label: 'Email, SMS & verify' },
              { val: 'TCPA', label: 'Compliance built-in' },
            ].map(({ val, label }) => (
              <div key={val} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center hover:shadow-md transition-shadow">
                <p className="text-2xl font-bold text-gray-900 mb-0.5">{val}</p>
                <p className="text-xs text-gray-500 font-medium">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────── SOCIAL PROOF */}
      <section className="bg-gray-950 py-5 px-6 border-y border-gray-800">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
          <p className="text-gray-500 text-sm font-medium">Replacing your stack of:</p>
          {['Cold email tool', 'SMS platform', 'Email verifier', 'DNC manager'].map(tool => (
            <div key={tool} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
              <span className="text-gray-300 text-sm font-medium">{tool}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────── FEATURES */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-indigo-600 text-sm font-bold uppercase tracking-widest mb-3">Everything you need</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
              One platform. Every channel.
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Stop paying for 4 separate tools. Outreach Engine replaces them all.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Mail,
                gradient: 'from-blue-500 to-indigo-600',
                bg: 'bg-blue-50',
                title: 'Cold Email Campaigns',
                desc: 'Multi-step sequences with personalisation tokens, scheduling, and full delivery tracking. Monitor opens, clicks, and replies in real time.',
              },
              {
                icon: MessageSquare,
                gradient: 'from-emerald-500 to-teal-600',
                bg: 'bg-emerald-50',
                title: 'SMS Outreach',
                desc: 'Reach prospects via text. TCPA-compliant opt-out happens automatically — STOP replies mark contacts as DNC instantly.',
              },
              {
                icon: ShieldCheck,
                gradient: 'from-violet-500 to-purple-600',
                bg: 'bg-violet-50',
                title: 'Email Verification',
                desc: 'Verify prospect emails with Hunter.io before sending. Protect your sender reputation and slash bounce rates at scale.',
              },
              {
                icon: Users,
                gradient: 'from-orange-500 to-red-500',
                bg: 'bg-orange-50',
                title: 'Prospect Management',
                desc: 'Organise into groups, track full contact history, manage DNC lists, and bulk-import via CSV in seconds.',
              },
              {
                icon: BarChart2,
                gradient: 'from-indigo-500 to-blue-600',
                bg: 'bg-indigo-50',
                title: 'Analytics & Tracking',
                desc: 'Open rates, reply rates, bounce rates and campaign performance — all in one dashboard. Know what\'s working.',
              },
              {
                icon: Zap,
                gradient: 'from-amber-500 to-yellow-500',
                bg: 'bg-amber-50',
                title: 'Automation Rules',
                desc: 'Trigger follow-ups, tag prospects, and move contacts through your pipeline automatically based on behaviour.',
              },
            ].map(({ icon: Icon, gradient, bg, title, desc }) => (
              <div
                key={title}
                className="group relative bg-white rounded-2xl border border-gray-100 p-7 hover:border-indigo-100 hover:shadow-lg hover:shadow-indigo-50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center mb-5`}>
                  <div className={`w-6 h-6 bg-gradient-to-br ${gradient} rounded-md flex items-center justify-center`}>
                    <Icon size={14} className="text-white" />
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────── HOW IT WORKS */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-indigo-600 text-sm font-bold uppercase tracking-widest mb-3">Simple process</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
              Up and running in minutes
            </h2>
            <p className="text-lg text-gray-500">Three steps from sign-up to your first campaign.</p>
          </div>

          <div className="relative grid md:grid-cols-3 gap-10">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-8 left-1/4 right-1/4 h-px bg-gradient-to-r from-indigo-200 via-violet-200 to-indigo-200" />

            {[
              {
                step: '01',
                title: 'Import your prospects',
                desc: 'Upload a CSV or search for prospects by industry, location and keyword. Verify emails before sending.',
                color: 'from-indigo-500 to-violet-600',
              },
              {
                step: '02',
                title: 'Build your campaign',
                desc: 'Create email and SMS templates, set sequence steps, and schedule your campaign at the right time.',
                color: 'from-violet-500 to-purple-600',
              },
              {
                step: '03',
                title: 'Track & close',
                desc: 'Monitor opens and replies in the unified inbox. Follow up at the perfect moment and convert.',
                color: 'from-purple-500 to-indigo-600',
              },
            ].map(({ step, title, desc, color }) => (
              <div key={step} className="relative text-center">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <span className="text-white font-extrabold text-lg">{step}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────── PRICING PREVIEW */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-indigo-600 text-sm font-bold uppercase tracking-widest mb-3">Pricing</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
              Simple plans, every feature included
            </h2>
            <p className="text-lg text-gray-500">Choose your monthly credits. No per-seat fees, no hidden limits. Bring your own API keys.</p>
          </div>

          {plansError ? (
            <div className="text-center">
              <p className="text-gray-600 mb-3">We couldn't load the plans right now.</p>
              <button onClick={reload} className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">Try again</button>
            </div>
          ) : plans.length > 0 && (
            <PlanCards
              plans={plans}
              billing="monthly"
              onSelect={() => navigate('/pricing#plan')}
              buttonLabel={() => 'See plan details'}
            />
          )}

          <div className="text-center mt-8">
            <Link to="/pricing" className="inline-flex items-center gap-1.5 text-indigo-600 font-semibold text-sm hover:text-indigo-700">
              See full plan details
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────── WHY SWITCH */}
      <section className="py-24 px-6 bg-gray-950">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-indigo-400 text-sm font-bold uppercase tracking-widest mb-3">Why teams switch</p>
            <h2 className="text-4xl font-extrabold text-white tracking-tight mb-4">
              Built different. Priced fair.
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Most outreach tools make you bolt on extra services for search, verification and SMS. We bundle them.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: '💰',
                title: 'Credit-based pricing',
                desc: 'Pay for the prospects you find and verify, not for seats. Three plans, every feature on all of them, and one simple credit balance.',
              },
              {
                icon: '🔗',
                title: 'Email + SMS + Verification',
                desc: 'Most tools only do one channel. We bundle all three so you stop duct-taping separate services together.',
              },
              {
                icon: '✅',
                title: 'Compliance built-in',
                desc: 'Automatic STOP handling for SMS and one-click unsubscribe for email. CAN-SPAM and TCPA compliance without the effort.',
              },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="bg-gray-900 rounded-2xl p-7 border border-gray-800">
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="text-white font-bold mb-2 text-base">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────── FINAL CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-indigo-600 via-violet-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMxLjY1NyAwIDMgMS4zNDMgMyAzcy0xLjM0MyAzLTMgMy0zLTEuMzQzLTMtMyAxLjM0My0zIDMtM3oiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L2c+PC9zdmc+')] opacity-30" />
        <div className="relative max-w-2xl mx-auto text-center">
          <h2 className="text-4xl font-extrabold text-white mb-4 tracking-tight">
            Start reaching more prospects today
          </h2>
          <p className="text-indigo-200 text-lg mb-10">
            Create your account in under a minute. Cancel anytime.
          </p>
          <Link
            to="/pricing#plan"
            className="inline-flex items-center gap-2 bg-white text-indigo-600 font-bold text-base px-10 py-4 rounded-xl hover:bg-indigo-50 transition-all shadow-2xl hover:-translate-y-0.5"
          >
            Get Started
            <ArrowRight size={18} />
          </Link>
          <p className="mt-5 text-indigo-300 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-white underline underline-offset-2 hover:no-underline">
              Sign in
            </Link>
          </p>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────── FOOTER */}
      <footer className="bg-gray-950 border-t border-gray-800 py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Zap size={14} className="text-white" />
            </div>
            <span className="text-white font-bold text-sm">Outreach Engine</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/pricing" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Pricing</Link>
            <Link to="/login" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Sign In</Link>
            <Link to="/pricing#plan" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Get Started</Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Terms</Link>
            <Link to="/privacy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">Privacy</Link>
          </div>
          <p className="text-gray-600 text-sm">© {new Date().getFullYear()} Outreach Engine</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
